

import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, ComponentRef, EventEmitter, Inject, Injector, Input, OnChanges, OnDestroy, OnInit, Output, PLATFORM_ID, SimpleChanges, ViewChild, ViewContainerRef, ViewEncapsulation, afterNextRender, createNgModule, } from '@angular/core';
import { EnvironmentActions } from '@app/core/actions';
import { GoogleTagManagerService } from '@app/core/lib/google-tag-manager/angular-google-tag-manager.service';
import * as fromRoot from '@app/reducers';
import { buildArrayFromRange } from '@app/shared/helpers/helpers';
import { select, Store } from '@ngrx/store';
import { PlyrComponent } from 'ngx-plyr-mg';
import { MediaType, Options, Source, SourceInfo } from 'plyr';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { Constant } from 'src/constant';

const DEFAULT_SAMPLE_RATE = 1000;

@Component({
  selector: 'app-smash-player',
  templateUrl: './smash-player.component.html',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule],
  styleUrls: ['./smash-player.component.scss']
})
export class SmashPlayerComponent implements OnInit, OnDestroy {

  @Input() source: SourceInfo;
  @Input() options: Options;
  @Input() allowConcurrency = false;
  @Input() fullscreen = false;
  @Input() unstoppable = false;
  @Input() context?: 'cover' | 'preview-customization' | 'promotion' | 'reveal';
  @Input() playerId?: string; // whether the player should be identified as unique (needed when multiple players are loaded on the same page)
  @Output() loading: EventEmitter<any> = new EventEmitter<any>();
  @Output() readyToPlay: EventEmitter<any> = new EventEmitter<any>();
  @Output() ended: EventEmitter<any> = new EventEmitter<any>();
  @Output() plyrEnterFullScreen: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() plyrExitFullScreen: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('_plyrRef', { static: true, read: ViewContainerRef }) _plyrRef!: ViewContainerRef;


  isPlyrLoaded: boolean = false;
  subscriptions: Subscription[] = [];

  player: Plyr;
  playerSources: Source[] = [];
  playerOptions: Options;
  playerType: string;
  playerProvider: Plyr.Provider;
  playerShouldLoop = false;

  checkPlayerCompletionInterval: NodeJS.Timeout;
  completionPercentage = 0;
  completionsRates = buildArrayFromRange(0, 100, 5);
  completionRatesChecked = {};

  componentRef: ComponentRef<PlyrComponent>
  constructor(
    private readonly store$: Store<fromRoot.State>,
    private gtmService: GoogleTagManagerService,
    @Inject(PLATFORM_ID) private platformId: object,
    private injector: Injector,
  ) {
    afterNextRender(async () => {
      const { PlyrComponent, PlyrModule } = await import('ngx-plyr-mg');
      const moduleRef = createNgModule(PlyrModule, this.injector);
      this.componentRef = this._plyrRef.createComponent(PlyrComponent, { ngModuleRef: moduleRef });
      const classList = ['player', `${this.playerProvider}-player`, `${this.fullscreen ? 'player-fullscreen' : 'player-normal'}`];
      classList.map(classValue => this.componentRef.location.nativeElement.classList.add(classValue));

      if (this.context) {
        this.componentRef.location.nativeElement.classList.add(`${this.context}-context`);
      }

      this.componentRef.setInput('plyrPlaysInline', true);
      this.componentRef.setInput('plyrType', this.playerType as MediaType);
      this.componentRef.setInput('plyrSources', this.playerSources);
      this.componentRef.setInput('plyrOptions', this.playerOptions);

      this.subscriptions.push(
        this.componentRef.instance.plyrInit.subscribe(($event) => this.initPlayer($event)),
        this.componentRef.instance.plyrLoadStart.subscribe(($event) => this.loadStart($event)),
        this.componentRef.instance.plyrCanPlay.subscribe(($event) => this.canPlay($event)),
        this.componentRef.instance.plyrEnded.subscribe(($event) => this.onEnd($event)),
        this.componentRef.instance.plyrEnterFullScreen.subscribe(($event) => this.onPlyrEnterFullScreen($event)),
        this.componentRef.instance.plyrExitFullScreen.subscribe(($event) => this.onPlyrExitFullScreen($event)),
      )
    })
  }

  ngOnInit(): void {
    if (isPlatformBrowser(this.platformId)) {
      this.playerId = this.playerId || btoa(this.source.sources[0].src);
      this.playerSources = this.source.sources;
      this.playerProvider = this.source.sources[0].provider; // FIX ME
      this.playerOptions = { ...this.options };
      this.playerType = this.source.type;
      if (this.playerProvider === 'youtube' && this.playerOptions?.loop?.active === true) {
        this.playerOptions.loop.active = false;
        this.playerShouldLoop = true;
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    /* This is a way to change source from player if the source has the same provider (vimeo/youtube) as the previous source */
    if (!!changes?.source?.currentValue) {
      this.playerSources = this.source.sources;
      this.playerType = this.source.type;
      if (this.componentRef) {
        this.componentRef.setInput('plyrSources', this.playerSources);
        this.loadStart(true);
      }
    }
    if (!!changes?.options?.currentValue) {
      this.playerOptions = this.options;
    }
  }

  initPlayer(event) {
    if (isPlatformBrowser(this.platformId)) {
      this.player = event;
      this.registerPlayer();
      this.listenToEvents();
      this.mutePlayerIfNeeded();
      this.disablePictureInPicture();
    }
  }

  registerPlayer(): void {
    this.store$.dispatch(EnvironmentActions.AddPlayer({
      id: this.playerId,
      url: this.source.sources[0].src,
      allowConcurrency: this.allowConcurrency,
      unstoppable: this.unstoppable
    }));
  }

  pushGtmCompletionEvent(completionRate: number): void {
    this.gtmService.pushTag({
      event: Constant.eventGTM.VIEW_VIDEO_COMPLETION,
      video_completion: completionRate,
      video_duration: this.player.duration,
      video_current_time: this.player.currentTime,
      video_id: btoa(this.source.sources[0].src),
      video_provider: this.source.sources[0].provider,
      video_title: (this.player as any)?.config ? (this.player as any).config.title : '',
    });
  }

  listenToEvents() {
    this.player.on('ready', (event) => {
      this.canPlay(event);
      if (this.options.autoplay) {
        // FIX ME 29072021 Update on Youtube player ? Autoplay doesn't seem to work well...
        this.player.play();
      }
    });

    this.player.on('exitfullscreen', (event) => {
      this.onPlyrExitFullScreen(event);
    });

    // FIX ME workaround for loop on Youtube video that makes froze .5s before playing again...
    this.player.on('ended', (event) => {
      if (this.playerProvider === 'youtube' && this.playerShouldLoop) {
        this.player.play();
      }
      if (!this.completionRatesChecked[100]) {
        this.completionRatesChecked[100] = true;
        this.pushGtmCompletionEvent(100);
        this.endCheckCompletion();
      }
    });

    this.player.on('playing', (event) => {
      this.startCheckCompletion();
      this.store$.dispatch(EnvironmentActions.UpdatePlayerPlayingAndStopOthers({ id: this.playerId }));
    });
    this.subscriptions.push(
      this.store$.pipe(select(fromRoot.getPlayer(this.playerId))).pipe(distinctUntilChanged()).subscribe(res => {
        if (res?.playing === false) {
          this.pause();
        }
      })
    );
  }

  mutePlayerIfNeeded(): void {
    if (this.player && this.playerProvider === 'youtube' || this.playerProvider === 'vimeo' || this.playerProvider === 'html5') {
      this.player.muted = true;
    }
  }

  play() {
    this.player.play();
  }

  pause() {
    this.player.pause();
  }

  fullscreenEnter() {
    this.player.fullscreen.enter();
  }

  fullscreenExit() {
    this.player.fullscreen.exit();
  }

  destroy() {
    this.player.destroy();
  }

  loadStart(event) {
    this.isPlyrLoaded = false;
    this.loading.emit(event);
  }

  canPlay(event) {
    if (!this.isPlyrLoaded) {
      this.readyToPlay.emit(event);
    }
    this.isPlyrLoaded = true;
  }

  onEnd(event) {
    this.ended.emit(event);
  }

  onPlyrEnterFullScreen(event): void {
    this.componentRef.location.nativeElement.classList.add('player-fullscreen');
    this.componentRef.location.nativeElement.classList.remove('player-normal');
    this.plyrEnterFullScreen.emit(event);
  }

  onPlyrExitFullScreen(event): void {
    this.componentRef.location.nativeElement.classList.add('player-normal');
    this.componentRef.location.nativeElement.classList.remove('player-fullscreen');
    this.plyrExitFullScreen.emit(event);
  }

  startCheckCompletion() {
    this.checkPlayerCompletionInterval = setInterval(() => {
      this.checkPlayerCompletion();
    }, DEFAULT_SAMPLE_RATE);
  }

  endCheckCompletion() {
    clearInterval(this.checkPlayerCompletionInterval);
  }

  checkPlayerCompletion() {
    if (!this.player || !this.player.duration || !this.player.currentTime) {
      return;
    }
    const duration = this.player.duration;
    const percentage = (this.player.currentTime / duration) * 100;
    this.completionPercentage = Math.round((percentage + Number.EPSILON) * 100) / 100;
    this.completionsRates.forEach((rate, index) => {
      if (this.completionPercentage >= rate && !this.completionRatesChecked[rate]) {
        this.completionRatesChecked[rate] = true;
        this.pushGtmCompletionEvent(rate);
      }
    });
  }

  get isPaused(): boolean {
    return this.player?.paused;
  }

  get isPlaying(): boolean {
    return this.player?.playing;
  }

  ngOnDestroy(): void {
    if (this.player) {
      this.player.destroy();
    }
    this.endCheckCompletion();
    this.subscriptions.map(subscription => subscription.unsubscribe());
    this.subscriptions.length = 0;
  }

  disablePictureInPicture() {
    const videoTag = this.componentRef.location.nativeElement.querySelector('video');
    if (videoTag) {
      videoTag.setAttribute('disablePictureInPicture', 'true');
    }
  }

}
